import { type RefObject, useCallback, useMemo, useRef } from 'react';

import {
  AR_COMPANY_FIRST_ROW_SELECTOR,
  DATA_TABLE_FIRST_ROW_SELECTOR,
  DATA_TABLE_TOOLBAR_KEYBOARD_NAV_ATTR,
} from './utils';

export type UseDataTableToolbarKeyboardNavOptions = {
  /**
   * CSS selector for the first focusable row when pressing Arrow Down in search.
   * @default `tbody tr[data-row-index="0"]` (standard DataTable)
   */
  firstRowSelector?: string;
};

export type DataTableToolbarKeyboardNavBindings = {
  searchInputRef: RefObject<HTMLInputElement | null>;
  focusSearch: () => void;
  /** Spread on a parent wrapping toolbar + table (e.g. CardBody). */
  rootProps: { [DATA_TABLE_TOOLBAR_KEYBOARD_NAV_ATTR]: '' };
  toolbarProps: { searchInputRef: RefObject<HTMLInputElement | null> };
  tableProps: { previousFocusRef: RefObject<HTMLInputElement | null> };
};

/**
 * Opt-in keyboard navigation between a DataTableToolbar search field and the first table row.
 *
 * @example
 * const nav = useDataTableToolbarKeyboardNav();
 * return (
 *   <CardBody className="space-y-4" {...nav.rootProps}>
 *     <DataTableToolbar table={table} searching={searching} {...nav.toolbarProps} />
 *     <DataTable table={table} {...nav.tableProps} />
 *   </CardBody>
 * );
 */
export function useDataTableToolbarKeyboardNav(
  options: UseDataTableToolbarKeyboardNavOptions = {}
): DataTableToolbarKeyboardNavBindings {
  const firstRowSelector = options.firstRowSelector ?? DATA_TABLE_FIRST_ROW_SELECTOR;
  const searchInputRef = useRef<HTMLInputElement>(null);

  const focusSearch = useCallback(() => {
    searchInputRef.current?.focus();
  }, []);

  return useMemo(
    () => ({
      searchInputRef,
      focusSearch,
      rootProps: {
        [DATA_TABLE_TOOLBAR_KEYBOARD_NAV_ATTR]: '' as const,
        'data-first-row-selector': firstRowSelector,
      },
      toolbarProps: { searchInputRef },
      tableProps: { previousFocusRef: searchInputRef },
    }),
    [firstRowSelector, focusSearch]
  );
}

/** Preset for AccountReceivableCompanyTable (expandable company rows). */
export function useArCompanyTableToolbarKeyboardNav(): DataTableToolbarKeyboardNavBindings {
  return useDataTableToolbarKeyboardNav({ firstRowSelector: AR_COMPANY_FIRST_ROW_SELECTOR });
}
